import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { useGetContractTermSummaryLazyQuery } from '../../../types';
import { ActiveStatus } from '../../../utils/contract-utils';

interface UseSubscriptionImpactCheckArgs {
  activeStatus: ActiveStatus;
  editId: number | undefined;
  originalStartDate: string | undefined;
  watchedStartDate: Date | null;
}

interface UseSubscriptionImpactCheckResult {
  subscriptionImpactError: string | null;
  subscriptionCheckPending: boolean;
}

export const useSubscriptionImpactCheck = ({
  activeStatus,
  editId,
  originalStartDate,
  watchedStartDate,
}: UseSubscriptionImpactCheckArgs): UseSubscriptionImpactCheckResult => {
  const [getContractTermSummary] = useGetContractTermSummaryLazyQuery();

  const [subscriptionImpactError, setSubscriptionImpactError] = useState<
    string | null
  >(null);
  const [subscriptionCheckPending, setSubscriptionCheckPending] =
    useState(false);

  const watchedStartDateStr = watchedStartDate
    ? dayjs(watchedStartDate).format('YYYY-MM-DD')
    : null;

  useEffect(() => {
    if (
      activeStatus !== ActiveStatus.Active ||
      !editId ||
      !originalStartDate ||
      !watchedStartDateStr
    ) {
      setSubscriptionImpactError(null);
      return;
    }

    const newStartDate = dayjs(watchedStartDateStr);
    const origStart = dayjs(originalStartDate);

    if (newStartDate.isSame(origStart, 'day')) {
      setSubscriptionImpactError(null);
      return;
    }

    if (newStartDate.isBefore(origStart, 'day')) {
      setSubscriptionImpactError(
        'Start date cannot be moved before the original start date',
      );
      return;
    }

    const checkEndDate = newStartDate.subtract(1, 'day').format('YYYY-MM-DD');

    setSubscriptionCheckPending(true);
    getContractTermSummary({
      variables: {
        contractId: editId,
        startDate: origStart.format('YYYY-MM-DD'),
        endDate: checkEndDate,
      },
      onCompleted: data => {
        setSubscriptionCheckPending(false);
        const { numSubscriptions, numFeeForServiceEvents } =
          data.getContractTermSummary;
        if (numSubscriptions > 0 || numFeeForServiceEvents > 0) {
          setSubscriptionImpactError(
            `${numSubscriptions} subscription(s) and ${numFeeForServiceEvents} fee-for-service event(s) exist before this date`,
          );
        } else {
          setSubscriptionImpactError(null);
        }
      },
      onError: () => {
        setSubscriptionCheckPending(false);
        setSubscriptionImpactError(
          'Unable to verify subscription impact, please try again',
        );
      },
    });
  }, [
    activeStatus,
    editId,
    originalStartDate,
    watchedStartDateStr,
    getContractTermSummary,
  ]);

  return { subscriptionImpactError, subscriptionCheckPending };
};
